import React, { createContext, useCallback, useContext, useState } from 'react';

import { noop } from 'lodash';

import { IBaseProps } from '@rbi-ctg/frontend';
import { ILoyaltyCustomizableContent } from '@rbi-ctg/menu';
import { getLoyaltyCustomizableContent } from 'remote/api/customizable-content';

import { useLocale } from '../intl';
import { useNetworkContext } from '../network';

interface ILoyaltyCustomizableContentState {
  loyaltyContent: ILoyaltyCustomizableContent;
  isLoadingLoyalty: boolean;
}

interface IGetLoyaltyRequest {
  requestLoyaltyCustomizableContent: () => void;
}

export type TCustomizableContentContext = ILoyaltyCustomizableContentState & IGetLoyaltyRequest;

export const CustomizableContentContext = createContext<TCustomizableContentContext>({
  loyaltyContent: {} as ILoyaltyCustomizableContent,
  isLoadingLoyalty: true,
  requestLoyaltyCustomizableContent: noop,
});

export const useCustomizableContent = () => useContext(CustomizableContentContext);

export function CustomizableContentProvider({ children }: IBaseProps) {
  const { query, defaultErrorHandler } = useNetworkContext();
  const { locale } = useLocale();

  const [loyaltyCustomizableContent, setLoyaltyCustomizableContent] =
    useState<ILoyaltyCustomizableContentState>({
      loyaltyContent: {} as ILoyaltyCustomizableContent,
      isLoadingLoyalty: true,
    });

  const requestLoyaltyCustomizableContent = useCallback(() => {
    query(getLoyaltyCustomizableContent())
      .then(loyaltyContent => {
        setLoyaltyCustomizableContent({
          loyaltyContent,
          isLoadingLoyalty: false,
        });
      })
      .catch(error => {
        defaultErrorHandler(error);
        setLoyaltyCustomizableContent(currentContent => ({
          ...currentContent,
          isLoadingLoyalty: false,
        }));
      });
  }, [locale]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <CustomizableContentContext.Provider
      value={{
        ...loyaltyCustomizableContent,
        requestLoyaltyCustomizableContent,
      }}
    >
      {children}
    </CustomizableContentContext.Provider>
  );
}

export default CustomizableContentContext.Consumer;
